import { CustomerOrderPackageView } from "src/api/generated/erp/db/types/tables/customerOrderPackageView.ts";
import {
	dateTimeColumn,
	enumColumn,
	floatColumn,
	integerColumn,
	textColumn,
} from "../../../../common/dataGrid/columns.tsx";
import i18n from "i18next";
import { getCustomerOrderPackageStateLabels } from "src/api/generated/erp/db/types/enums/customerOrderPackageState.ts";
import { dataGridPersistentStateProps } from "../../../../common/dataGrid/dataGridStateStorage.ts";
import { customerOrderPackageTypeLabels } from "./pickingUtils.ts";
import { CustomerOrderPackageForm } from "./CustomerOrderPackageForm.tsx";
import { CustomerOrderPackageDataGridApi } from "src/api/generated/erp/sales/picking/api/customerOrderPackageDataGridApi.ts";
import { DocumentsOfObjectButton } from "../../../documents/objectDocuments/DocumentsOfObjectButton.tsx";
import { AsyncButton } from "../../../../common/buttons/AsyncButton.tsx";
import { CustomerOrderPackageApi } from "src/api/generated/erp/sales/picking/api/customerOrderPackageApi.ts";
import { faCalculator, faCheck, faPrint, faUndo } from "@fortawesome/pro-regular-svg-icons";
import { ControlledAsyncCrudDataGrid } from "../../../../common/dataGrid/crud/ControlledAsyncCrudDataGrid.tsx";
import { downloadFile } from "src/utils/fileDownloading.ts";
import { useAsyncDataGridModel } from "src/components/common/dataGrid/useAsyncDataGridModel.ts";

export interface UndeliveredCustomerOrderPackagesDataGridProps {
	customerOrderId: number;
	onSelectedPackageChanged?: (row: CustomerOrderPackageView | undefined) => void;
	pickableLinesDataGridRefreshRef?: () => void;
	packagesDataGridRefreshRef?: () => void;
}

export const UndeliveredCustomerOrderPackagesDataGrid = ({
	customerOrderId,
	onSelectedPackageChanged,
	pickableLinesDataGridRefreshRef,
	packagesDataGridRefreshRef,
}: UndeliveredCustomerOrderPackagesDataGridProps) => {
	const { refresh, dataGridProps, onlySelectedRow } = useAsyncDataGridModel({
		fetchData: () =>
			CustomerOrderPackageDataGridApi.getCustomerOrderPackages({
				customerOrderId: customerOrderId,
				stateFilter: ["PARTIALLY_PICKED", "PICKED"],
			}),
		initialParams: {},
		getRows: (data) => data,
		getRowId: (row) => row.customerOrderPackageId,
		onRowSelectionChanged: (rows) => {
			onSelectedPackageChanged?.(rows[0]);
		},
	});

	return (
		<ControlledAsyncCrudDataGrid<CustomerOrderPackageView>
			{...dataGridProps}
			remove={{
				type: "enabled",
				action: async (params) => {
					await CustomerOrderPackageDataGridApi.delete_({
						customerOrderPackages: params.items,
					});
				},
			}}
			disableMultipleRowSelection
			columns={[
				integerColumn({
					field: "packageNo",
					headerName: i18n.t("package_no"),
				}),
				textColumn({
					field: "description",
					headerName: i18n.t("description"),
					width: 300,
				}),
				enumColumn({
					field: "type",
					headerName: i18n.t("type"),
					enumLabels: customerOrderPackageTypeLabels,
				}),
				enumColumn({
					field: "customerOrderPackageState",
					headerName: i18n.t("state"),
					enumLabels: getCustomerOrderPackageStateLabels(),
					width: 125,
				}),
				textColumn({
					field: "locationCode",
					headerName: i18n.t("warehouse_location"),
					width: 120,
				}),
				floatColumn({
					field: "weight",
					headerName: i18n.t("weight"),
				}),
				floatColumn({
					field: "length",
					headerName: i18n.t("length"),
				}),
				floatColumn({
					field: "width",
					headerName: i18n.t("width"),
				}),
				floatColumn({
					field: "height",
					headerName: i18n.t("height"),
				}),
				textColumn({
					field: "createdByUserName",
					headerName: i18n.t("created_by"),
				}),
				dateTimeColumn({
					field: "createdDate",
					headerName: i18n.t("created_at"),
					width: 150,
				}),
				dateTimeColumn({
					field: "pickedAt",
					headerName: i18n.t("picked_at"),
					width: 150,
				}),
			]}
			form={{
				dialogSize: "md",
				dialogTitle: i18n.t("package"),
				addRowEnabled: true,
				editEnabled: true,
				component: ({ row, onCompleted, onFormEdited }) => {
					return (
						<CustomerOrderPackageForm
							customerOrderId={customerOrderId}
							customerOrderPackageId={row?.customerOrderPackageId}
							onCompleted={(result) => {
								onCompleted(result);
							}}
							onFormEdited={onFormEdited}
						/>
					);
				},
			}}
			{...dataGridPersistentStateProps("C25DEE4F58A5261")}
			actionBarComponents={
				<>
					<DocumentsOfObjectButton
						objectRef={{
							objectType: "CUSTOMER_ORDER",
							objectId: customerOrderId,
						}}
						label={i18n.t("order_documents")}
						variant={"outlined"}
						editable={false}
					/>
					{(!onlySelectedRow ||
						onlySelectedRow.customerOrderPackageState === "PARTIALLY_PICKED") && (
						<AsyncButton
							label={i18n.t("mark_picked")}
							variant={"outlined"}
							icon={faCheck}
							disabled={() => {
								if (!onlySelectedRow) return i18n.t("select_line");
								return false;
							}}
							onClick={async () => {
								if (!onlySelectedRow) return;
								await CustomerOrderPackageApi.setPackageStateToPicked({
									customerOrderPackageId: onlySelectedRow.customerOrderPackageId,
								});
								pickableLinesDataGridRefreshRef?.();
								packagesDataGridRefreshRef?.();
								await refresh();
							}}
						/>
					)}
					{onlySelectedRow?.customerOrderPackageState === "PICKED" && (
						<AsyncButton
							label={i18n.t("return_open")}
							variant={"outlined"}
							icon={faUndo}
							disabled={() => {
								if (!onlySelectedRow) return i18n.t("select_line");
								return false;
							}}
							onClick={async () => {
								if (!onlySelectedRow) return;
								await CustomerOrderPackageApi.setPackageStateToUnpicked({
									customerOrderPackageId: onlySelectedRow.customerOrderPackageId,
								});
								pickableLinesDataGridRefreshRef?.();
								packagesDataGridRefreshRef?.();
								await refresh();
							}}
						/>
					)}
					<AsyncButton
						icon={faPrint}
						label={i18n.t("print_package_label")}
						variant={"outlined"}
						disabled={() => {
							if (!onlySelectedRow) return i18n.t("select_line");
							return false;
						}}
						onClick={async () => {
							if (!onlySelectedRow) return;
							const fileHandle =
								await CustomerOrderPackageApi.printCustomerOrderPackageLabel({
									customerOrderPackageId: onlySelectedRow.customerOrderPackageId,
								});
							downloadFile(fileHandle);
						}}
					/>
					<AsyncButton
						icon={faCalculator}
						label={i18n.t("calculate_weight")}
						variant={"outlined"}
						disabled={() => {
							if (!onlySelectedRow) return i18n.t("select_line");
							return false;
						}}
						onClick={async () => {
							if (!onlySelectedRow) return;
							await CustomerOrderPackageApi.calculateCustomerOrderPackageWeight({
								customerOrderPackageId: onlySelectedRow.customerOrderPackageId,
							});
							await refresh();
						}}
					/>
				</>
			}
		/>
	);
};
